import * as React from 'react';
import { Component, ErrorInfo, ReactNode } from 'react';

import { logError } from '@/utils/errorLogger';

import ErrorBoundaryDisplay from './ErrorBoundaryDisplay';

export interface ErrorBoundaryProps {
  source?: string;
  children: ReactNode;
  fallback?: ReactNode;
  fallbackRender?: (props: {
    error: Error;
    resetError: () => void;
    errorInfo: ErrorInfo | null;
    lastErrorAt: string | null;
    source: string;
  }) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  resetKeys?: unknown;
  onRetry?: () => void;
  onHardRefresh?: () => void;
  contextMetadata?: Record<string, unknown>;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  lastErrorAt: string | null;
}

const arrayHasChanged = (current: unknown = [], previous: unknown = []) => {
  if (!Array.isArray(current) || !Array.isArray(previous)) {
    return false;
  }
  if (current.length !== previous.length) {
    return true;
  }
  return current.some((value, index) => value !== previous[index]);
};

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      lastErrorAt: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const payload = logError(error, {
      source: this.props.source ?? 'ErrorBoundary',
      context: {
        componentStack: errorInfo.componentStack,
        boundarySource: this.props.source,
        boundaryContext: this.props.contextMetadata,
      },
    });

    this.setState({
      errorInfo,
      lastErrorAt: payload?.timestamp ?? new Date().toISOString(),
    });

    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && arrayHasChanged(this.props.resetKeys, prevProps.resetKeys)) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = (): void => {
    if (typeof this.props.onReset === 'function') {
      try {
        this.props.onReset();
      } catch (error) {
        logError(error as Error, {
          source: 'ErrorBoundary:onReset',
          severity: 'warn',
        });
      }
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      lastErrorAt: null,
    });
  };

  override render(): ReactNode {
    const { hasError, error, errorInfo, lastErrorAt } = this.state;
    const { children, fallback, fallbackRender, onRetry, onHardRefresh, source } = this.props;

    if (hasError) {
      // If a fallback element is provided, use it directly
      if (fallback) {
        return fallback;
      }

      // Otherwise use fallbackRender function if provided
      if (typeof fallbackRender === 'function' && error) {
        return fallbackRender({
          error,
          resetError: this.resetErrorBoundary,
          errorInfo,
          lastErrorAt,
          source: source ?? 'ErrorBoundary',
        });
      }

      const handleRetry =
        onRetry ??
        (() => {
          this.resetErrorBoundary();
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
        });

      const handleHardRefresh =
        onHardRefresh ??
        (() => {
          if (typeof window !== 'undefined') {
            window.location.href = window.location.href + '?retry=' + Date.now();
          }
        });

      return (
        <ErrorBoundaryDisplay
          error={error}
          timestamp={lastErrorAt}
          onRetry={handleRetry}
          onHardRefresh={handleHardRefresh}
        />
      );
    }

    return children;
  }
}
